import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingBag, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getImageUrl } from '../utils/imageUrl';

export const ProductCard = ({ product, onQuickAdd, priority = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  const allergenColors = {
    gluten: 'bg-amber-100 text-amber-800',
    eggs: 'bg-yellow-100 text-yellow-800',
    dairy: 'bg-blue-100 text-blue-800',
    nuts: 'bg-orange-100 text-orange-800',
  };

  const stock = product.stock_quantity;
  const isSoldOut = stock !== null && stock !== undefined && stock <= 0;
  const isLowStock = stock !== null && stock !== undefined && stock > 0 && stock <= 3;

  return (
    <div
      className={`group relative bg-white rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 border ${
        isSoldOut ? 'border-red-200' : 'border-stone-100'
      }`}
      data-testid={`product-card-${product.id}`}
    >
      {/* SOLD OUT overlay */}
      {isSoldOut && (
        <div
          className="absolute inset-0 z-20 bg-black/55 backdrop-blur-[1px] flex items-center justify-center pointer-events-none"
          data-testid={`product-soldout-${product.id}`}
        >
          <span className="rotate-[-8deg] font-['Playfair_Display'] text-2xl sm:text-3xl font-bold tracking-[0.2em] text-white border-2 border-white px-5 py-2 bg-red-700/60">
            SOLD OUT
          </span>
        </div>
      )}

      {/* Low stock badge */}
      {isLowStock && !isSoldOut && (
        <div className="absolute top-2 left-2 z-10">
          <span
            className="bg-amber-500 text-white text-[10px] sm:text-xs font-bold tracking-wider uppercase px-2.5 py-1 rounded-full shadow-md"
            data-testid={`product-lowstock-${product.id}`}
          >
            Only {stock} left
          </span>
        </div>
      )}

      {/* Image */}
      <Link to={`/product/${product.id}`} className="block aspect-[4/3] overflow-hidden bg-stone-100 relative">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-stone-200 animate-pulse" />
        )}
        <img
          src={getImageUrl(product.image)}
          alt={product.name}
          loading={priority ? "eager" : "lazy"}
          fetchpriority={priority ? "high" : "auto"}
          decoding="async"
          onLoad={() => setImageLoaded(true)}
          className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      </Link>

      {/* Quick Add Button - appears on hover (desktop only) */}
      {!product.customizable && !isSoldOut && (
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="icon"
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/90 hover:bg-white text-[#1a1a1a] shadow-lg"
            onClick={(e) => {
              e.preventDefault();
              onQuickAdd?.(product);
            }}
            data-testid={`quick-add-${product.id}`}
          >
            <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
        </div>
      )}

      {/* Content - Compact for mobile */}
      <div className="p-3 sm:p-5">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-['Playfair_Display'] text-sm sm:text-lg font-semibold text-[#1a1a1a] group-hover:text-[#8D6E63] transition-colors line-clamp-2">
            {product.name}
          </h3>
        </Link>
        
        <p className="text-stone-500 text-[11px] sm:text-sm mt-1 line-clamp-2 leading-snug">
          {product.description}
        </p>

        {/* Allergens */}
        {product.allergens?.length > 0 && (
          <div className="flex items-center gap-1 sm:gap-1.5 mt-2 sm:mt-3 flex-wrap">
            <AlertCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-stone-400 shrink-0" />
            {product.allergens.map((allergen) => (
              <Badge
                key={allergen}
                variant="secondary"
                className={`text-[9px] sm:text-xs px-1.5 sm:px-2 py-0 ${allergenColors[allergen] || 'bg-stone-100'}`}
              >
                {allergen}
              </Badge>
            ))}
          </div>
        )}

        {/* Price & CTA */}
        <div className="flex items-center justify-between mt-2 sm:mt-4 pt-2 sm:pt-4 border-t border-stone-100">
          <div>
            <span className="text-xs text-stone-500 hidden sm:inline">From</span>
            <p className="text-base sm:text-xl font-bold text-[#1a1a1a]">
              €{product.base_price.toFixed(2)}
            </p>
          </div>
          <Link to={`/product/${product.id}`} className={isSoldOut ? 'pointer-events-none' : ''}>
            <Button
              variant={product.customizable ? "default" : "outline"}
              size="sm"
              disabled={isSoldOut}
              className={`rounded-full text-xs sm:text-sm h-8 sm:h-10 px-3 sm:px-4 ${
                isSoldOut
                  ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
                  : product.customizable
                  ? 'bg-[#1a1a1a] hover:bg-[#1a1a1a]/90'
                  : 'border-[#8D6E63] text-[#8D6E63] hover:bg-[#8D6E63] hover:text-white'
              }`}
              data-testid={`view-product-${product.id}`}
            >
              {isSoldOut ? 'Sold out' : product.customizable ? 'Customize' : 'View'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
